import React, { Fragment } from 'react'
import { TextLink } from 'secondstep-components'
import PropTypes from 'prop-types'
import { domains } from 'config/env'
import { formatLastUpdatedRelativeTime } from 'utils/reportsHelpers'
import { TOOLTIP_LOCATIONS, TOOLTIP_PROGRAMS } from 'utils/tooltipHelpers'
import AdminExportDownloadButton from 'components/AdminExportDownloadButton'
import {
  Bold,
  Bullet,
  Classes,
  LastUpdatedMessage,
  LeftBox,
  LessonCompletionOverviewTitleRow,
  RightBox,
  SchoolTitle,
  SchoolsAndClasses,
  SiteHeaderWrapper,
  StyledHeaderOne,
  Summary,
  TierPill,
  Totals,
} from '../component.styles'
import {
  DIGITAL_WELL_BEING_UNIT_PILL_TEXT,
  LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL,
  SITE_HEADER_OVERVIEW_BASE,
} from '../constants'

const AdminSiteHeader = ({
  classesCreated,
  classesNotStarted,
  etlLastUpdatedAt: lastUpdatedAt,
  lessonCompletionUnitsLabel = 'Core Units',
  showLessonCompletionExport = true,
  singleSite,
  siteId,
  siteName,
  sitesTotal,
}) => {
  const classesNotStartedStr = `class${classesNotStarted === 1 ? '' : 'es'} ha${
    classesNotStarted === 1 ? `sn't` : `ven't`
  } completed a lesson yet`
  const classesStr = `Class${classesCreated === 1 ? '' : 'es'} Created`
  const lastUpdatedMessage = formatLastUpdatedRelativeTime(lastUpdatedAt)
  const schoolsStr = !singleSite && `School${sitesTotal === 1 ? '' : 's'}`

  const primaryHeaderTitle = `${SITE_HEADER_OVERVIEW_BASE}, ${lessonCompletionUnitsLabel}`
  const showSpecializedDwbPill =
    Boolean(lessonCompletionUnitsLabel) &&
    lessonCompletionUnitsLabel === LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL &&
    !singleSite

  const trainingReportHref = `${
    domains.ADMIN_DOMAIN
  }/training-report/${siteId}${
    lessonCompletionUnitsLabel === LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL
      ? '/digitalwellbeing'
      : '/core'
  }`

  return (
    <SiteHeaderWrapper>
      <LeftBox>
        {singleSite && <SchoolTitle>{siteName?.toUpperCase()}</SchoolTitle>}
        {showSpecializedDwbPill ? (
          <LessonCompletionOverviewTitleRow>
            <StyledHeaderOne $inTitleRow>{primaryHeaderTitle}</StyledHeaderOne>
            <TierPill data-testid="digital-well-being-unit-pill">
              <span>{DIGITAL_WELL_BEING_UNIT_PILL_TEXT}</span>
            </TierPill>
          </LessonCompletionOverviewTitleRow>
        ) : (
          <StyledHeaderOne>{primaryHeaderTitle}</StyledHeaderOne>
        )}
        <Summary>
          <SchoolsAndClasses>
            {!singleSite && (
              <Classes key="multi-site-1">
                <Bold>{sitesTotal}</Bold> {schoolsStr}
                <Bold>{` • `}</Bold>
                <Bold>{classesCreated}</Bold> {classesStr}
                {classesNotStarted !== 0 && (
                  <Fragment key="classes-not-started-key">
                    <span className="pipe">|</span>
                    <span>
                      <Bullet /> {classesNotStarted} {classesNotStartedStr}
                    </span>
                  </Fragment>
                )}
              </Classes>
            )}
            {singleSite && (
              <Totals>
                <Classes>
                  <Bold>{classesCreated}</Bold> {classesStr}
                </Classes>
                {classesNotStarted !== 0 && (
                  <Fragment key="classes-not-started-key">
                    <span className="pipe">|</span>
                    <Classes>
                      <Bullet /> {classesNotStarted} {classesNotStartedStr}
                    </Classes>
                  </Fragment>
                )}
              </Totals>
            )}
          </SchoolsAndClasses>
        </Summary>

        <TextLink
          aria-label="View training progress report"
          dataTestId="training-progress-report-link"
          href={trainingReportHref}
          target="_self"
        >
          Training Progress Report
        </TextLink>
      </LeftBox>
      <RightBox>
        <LastUpdatedMessage>{lastUpdatedMessage}</LastUpdatedMessage>
        {showLessonCompletionExport && classesCreated > 0 && (
          <AdminExportDownloadButton
            programKey={TOOLTIP_PROGRAMS.K8_MONITOR}
            tooltipLocation={TOOLTIP_LOCATIONS.EXPORT_BUTTON}
          />
        )}
      </RightBox>
    </SiteHeaderWrapper>
  )
}

AdminSiteHeader.propTypes = {
  classesCreated: PropTypes.number,
  classesNotStarted: PropTypes.number,
  etlLastUpdatedAt: PropTypes.string,
  lessonCompletionUnitsLabel: PropTypes.string,
  showLessonCompletionExport: PropTypes.bool,
  singleSite: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteName: PropTypes.string,
  sitesTotal: PropTypes.number,
}

export default AdminSiteHeader
